import BigNumber from "bignumber.js";
import { useMemo } from "react";
import { t } from "ttag";
import { parseUnits } from "viem";
import { useSimulateContract, useWriteContract } from "wagmi";

import { NULL_ADDRESS } from "@/constants/common";
import { CallType } from "@/constants/enum";
import {
  GAS_DEPOSIT_BASE,
  GAS_DEPOSIT_QUOTE,
  GAS_LONG,
  GAS_SHORT,
} from "@/constants/gas";
import { N_ONE, ONE } from "@/constants/math";
import { useRx } from "@/contexts/rx";
import { useSwapCallData } from "@/contracts/react/hooks";
import { getExecuteParams } from "@/contracts/tx/execute";
import { useFeatureFlags } from "@/hooks/feature";
import { useGas } from "@/hooks/gas";
import { usePushSubject } from "@/hooks/rx";
import type { TokenDetails } from "@/hooks/token";
import { FetchBalanceResult } from "@/types/common";
import { isNumStr } from "@/util/core";
import { markWritable } from "@/util/core";

import type { State, TradeToken } from "../state";

const SWAP_CALLDATA = {
  [CallType.DepositBase]: "DepositBase",
  [CallType.DepositQuote]: "DepositQuote",
  [CallType.Long]: "Long",
  [CallType.Short]: "Short",
} as const;

const getLongShortAmount = ({
  basePrice,
  direction,
  amount,
  leverage,
  token,
  flip,
}: {
  basePrice?: BigNumber;
  direction?: "long" | "short";
  amount?: BigNumber;
  leverage?: number;
  token?: TradeToken;
  flip?: boolean;
}) => {
  if (!amount?.isFinite() || !basePrice || !leverage || !direction || !token) {
    return;
  }

  const isReverse =
    (direction === "long" && token === "quote") ||
    (direction === "short" && token === "base");

  if (!flip && isReverse) {
    throw new Error("marginly v1 does not support flip");
  }

  if (leverage < 2) {
    throw new Error("unexpected leverage");
  }

  const leveraged = amount.times(leverage - 1);
  const sign = isReverse ? N_ONE : ONE;

  const lsAmount = sign.times(
    token === "base" ? leveraged : leveraged.div(basePrice),
  );

  return lsAmount;
};

export const useOpenPosition = ({
  address,
  balance,
  baseDecimals,
  basePrice,
  quoteDecimals,
  enabled: _enabled,
  realAmount,
  state,
  token,
  unwrap,
}: {
  address?: `0x${string}`;
  balance?: FetchBalanceResult;
  baseDecimals?: number;
  basePrice?: BigNumber;
  quoteDecimals?: number;
  enabled?: boolean;
  realAmount?: BigNumber;
  state?: State;
  token?: TokenDetails;
  /** @deprecated fixme decide if it's ok */
  unwrap?: `0x${string}`;
}) => {
  const { error$ } = useRx();
  const flip = useFeatureFlags(address)?.flip;

  const amount = useMemo(
    () =>
      state?.depositAmountBn
        ? state.depositAmountBn.minus(realAmount ?? 0)
        : undefined,
    [realAmount, state?.depositAmountBn],
  );

  const enabled =
    _enabled &&
    amount?.isFinite() &&
    amount.gte(0) &&
    amount.lte(balance?.formatted ?? 0);

  const isDeposit = amount
    ?.dp((state?.token === "base" ? baseDecimals : quoteDecimals) ?? 0)
    .gt(0);

  const callType = !state?.token
    ? undefined
    : state.token === "base"
    ? isDeposit
      ? CallType.DepositBase
      : CallType.Long
    : isDeposit
    ? CallType.DepositQuote
    : CallType.Short;

  const swapCallType = callType ? SWAP_CALLDATA[callType] : undefined;

  const { gas, gasPrice, useError } = useGas(
    callType === CallType.Long
      ? GAS_LONG
      : callType === CallType.Short
      ? GAS_SHORT
      : callType === CallType.DepositBase
      ? GAS_DEPOSIT_BASE
      : GAS_DEPOSIT_QUOTE,
  );

  const longShortAmount = getLongShortAmount({
    amount: state?.depositAmountBn,
    basePrice,
    flip,
    ...state,
  });
  const isFiniteTotalState = state?.positionSize?.total.isFinite();
  const haveBalance = Boolean(balance);
  const _amount = amount?.toString();
  const lsAmount = longShortAmount?.dp(baseDecimals!).toString();

  const args = useMemo(() => {
    if (
      typeof callType === "undefined" ||
      !haveBalance ||
      !isNumStr(_amount) ||
      !isFiniteTotalState ||
      !Number.isFinite(baseDecimals)
    ) {
      return;
    }

    if (lsAmount === undefined) {
      return;
    }

    const _args = markWritable([
      callType,
      isDeposit
        ? parseUnits(_amount, token?.decimals ?? 0)
        : parseUnits(lsAmount, baseDecimals!),
      isDeposit ? parseUnits(lsAmount, baseDecimals!) : BigInt(0),
      unwrap === token?.address,
      NULL_ADDRESS,
    ] as const);

    return _args;
  }, [
    _amount,
    callType,
    haveBalance,
    isFiniteTotalState,
    baseDecimals,
    lsAmount,
    isDeposit,
    token?.decimals,
    token?.address,
    unwrap,
  ]);

  const swapCallData = useSwapCallData({
    poolAddress: address,
    callType: swapCallType,
    exactAmount: (isDeposit
      ? parseUnits((longShortAmount ?? 0).toString(), baseDecimals ?? 0)
      : parseUnits(
          (state?.depositAmountBn?.toString(10) ?? 0).toString(),
          token?.decimals ?? 0,
        )
    ).toString(),
  });

  const simulateArgs = useMemo(() => {
    return {
      ...getExecuteParams(args, {
        basePrice,
        baseDecimals,
        quoteDecimals,
        swapCallData,
        version: flip ? "v15" : "v1",
      }),
      address: args && enabled ? address : undefined,
      query: {
        enabled: !!swapCallData && !!args && enabled,
      },
      gas,
      gasPrice,
      value: (unwrap === token?.address &&
      [CallType.DepositBase, CallType.DepositQuote].includes(callType!)
        ? args?.[1]
        : BigInt(0)) as undefined,
    } as const;
  }, [
    address,
    args,
    baseDecimals,
    basePrice,
    callType,
    enabled,
    flip,
    gas,
    gasPrice,
    quoteDecimals,
    swapCallData,
    token?.address,
    unwrap,
  ]);

  const open = useSimulateContract(simulateArgs);

  useError(open.error);
  const tx = useWriteContract();
  usePushSubject(error$, tx.error);

  return {
    amount,
    args,
    enabled,
    params: open,
    title: t`To open position`,
    tx,
  };
};
